'use client';

import React, { createContext, useContext, useReducer, useCallback, useMemo, useEffect } from 'react';
import type {
  DrawingShape,
  DrawingShapeStyle,
  DrawingToolType,
  DrawingMode,
  DrawingSyncScope,
  DrawingState,
  DrawingDataPoint,
} from './types';
import { DEFAULT_SHAPE_STYLE } from './types';

// Maximum history entries for undo/redo
const MAX_HISTORY_SIZE = 50;

// LocalStorage key for persisting drawings
const STORAGE_KEY_PREFIX = 'datamind-drawings';

// Helper: Load shapes from localStorage
const loadShapesFromStorage = (): DrawingShape[] => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    // Convert date strings back to Date objects if needed
    return parsed.map((shape: any) => {
      if (shape.start && typeof shape.start.x === 'string') {
        shape.start.x = new Date(shape.start.x);
      }
      if (shape.end && typeof shape.end.x === 'string') {
        shape.end.x = new Date(shape.end.x);
      }
      if (shape.position && typeof shape.position.x === 'string') {
        shape.position.x = new Date(shape.position.x);
      }
      // Handle other shape types...
      return shape;
    });
  } catch (error) {
    console.error('Failed to load drawings from localStorage:', error);
    return [];
  }
};

// Helper: Save shapes to localStorage
const saveShapesToStorage = (shapes: DrawingShape[]) => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY_PREFIX, JSON.stringify(shapes));
  } catch (error) {
    console.error('Failed to save drawings to localStorage:', error);
  }
};

// Initial state
const initialState: DrawingState = {
  mode: 'idle',
  activeTool: null,
  shapes: [],
  selectedShapeIds: [],
  activeChartId: null,
  tempShape: null,
  tempPoints: [],
  syncEnabled: false,
  syncScope: 'none',
  historyIndex: -1,
  history: [],
};

// Action types
type DrawingAction =
  | { type: 'SET_TOOL'; payload: DrawingToolType | null }
  | { type: 'SET_MODE'; payload: DrawingMode }
  | { type: 'SET_ACTIVE_CHART'; payload: string | null }
  | { type: 'ADD_SHAPE'; payload: DrawingShape }
  | { type: 'UPDATE_SHAPE'; payload: { id: string; updates: Partial<DrawingShape> } }
  | { type: 'DELETE_SHAPE'; payload: string }
  | { type: 'DELETE_SELECTED' }
  | { type: 'SELECT_SHAPE'; payload: { id: string; additive: boolean } }
  | { type: 'DESELECT_ALL' }
  | { type: 'UPDATE_SELECTED_STYLE'; payload: Partial<DrawingShapeStyle> }
  | { type: 'SET_TEMP_SHAPE'; payload: Partial<DrawingShape> | null }
  | { type: 'ADD_TEMP_POINT'; payload: DrawingDataPoint }
  | { type: 'CLEAR_TEMP_POINTS' }
  | { type: 'SET_SYNC_ENABLED'; payload: boolean }
  | { type: 'SET_SYNC_SCOPE'; payload: DrawingSyncScope }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'CLEAR_ALL' }
  | { type: 'CLEAR_CHART'; payload: string };

// Helper to save history
const saveToHistory = (state: DrawingState, newShapes: DrawingShape[]): DrawingState => {
  const newHistory = state.history.slice(0, state.historyIndex + 1);
  newHistory.push([...newShapes]);

  // Limit history size
  if (newHistory.length > MAX_HISTORY_SIZE) {
    newHistory.shift();
  }

  return {
    ...state,
    shapes: newShapes,
    history: newHistory,
    historyIndex: newHistory.length - 1,
  };
};

// Reducer
function drawingReducer(state: DrawingState, action: DrawingAction): DrawingState {
  switch (action.type) {
    case 'SET_TOOL': {
      const newMode = action.payload === null ? 'idle' : action.payload === 'select' ? 'idle' : 'ready';
      return {
        ...state,
        activeTool: action.payload,
        mode: newMode,
        selectedShapeIds: action.payload !== 'select' ? [] : state.selectedShapeIds,
      };
    }

    case 'SET_MODE':
      return { ...state, mode: action.payload };

    case 'SET_ACTIVE_CHART':
      return { ...state, activeChartId: action.payload };

    case 'ADD_SHAPE': {
      const newShapes = [...state.shapes, action.payload];
      return {
        ...saveToHistory(state, newShapes),
        mode: 'idle',
        tempShape: null,
        tempPoints: [],
      };
    }

    case 'UPDATE_SHAPE': {
      const newShapes = state.shapes.map((shape) =>
        shape.id === action.payload.id
          ? { ...shape, ...action.payload.updates, modifiedAt: Date.now() }
          : shape
      );
      return saveToHistory(state, newShapes as DrawingShape[]);
    }

    case 'DELETE_SHAPE': {
      const newShapes = state.shapes.filter((shape) => shape.id !== action.payload);
      return {
        ...saveToHistory(state, newShapes),
        selectedShapeIds: state.selectedShapeIds.filter((id) => id !== action.payload),
      };
    }

    case 'DELETE_SELECTED': {
      const newShapes = state.shapes.filter(
        (shape) => !state.selectedShapeIds.includes(shape.id)
      );
      return {
        ...saveToHistory(state, newShapes),
        selectedShapeIds: [],
        mode: 'idle',
      };
    }

    case 'SELECT_SHAPE': {
      const { id, additive } = action.payload;
      const isAlreadySelected = state.selectedShapeIds.includes(id);

      let newSelectedIds: string[];
      if (additive) {
        // Toggle selection
        newSelectedIds = isAlreadySelected
          ? state.selectedShapeIds.filter((sid) => sid !== id)
          : [...state.selectedShapeIds, id];
      } else {
        // Single selection
        newSelectedIds = isAlreadySelected ? [] : [id];
      }

      return {
        ...state,
        selectedShapeIds: newSelectedIds,
        mode: newSelectedIds.length > 0 ? 'selected' : 'idle',
      };
    }

    case 'DESELECT_ALL':
      return {
        ...state,
        selectedShapeIds: [],
        mode: state.activeTool === 'select' ? 'idle' : state.mode,
      };

    case 'UPDATE_SELECTED_STYLE': {
      if (state.selectedShapeIds.length === 0) return state;

      const newShapes = state.shapes.map((shape) =>
        state.selectedShapeIds.includes(shape.id)
          ? {
              ...shape,
              style: { ...shape.style, ...action.payload },
              modifiedAt: Date.now(),
            }
          : shape
      );
      return saveToHistory(state, newShapes as DrawingShape[]);
    }

    case 'SET_TEMP_SHAPE':
      return { ...state, tempShape: action.payload };

    case 'ADD_TEMP_POINT':
      return { ...state, tempPoints: [...state.tempPoints, action.payload] };

    case 'CLEAR_TEMP_POINTS':
      return { ...state, tempPoints: [], tempShape: null };

    case 'SET_SYNC_ENABLED':
      return { ...state, syncEnabled: action.payload };

    case 'SET_SYNC_SCOPE':
      return { ...state, syncScope: action.payload };

    case 'UNDO': {
      if (state.historyIndex <= 0) return state;
      const newIndex = state.historyIndex - 1;
      return {
        ...state,
        shapes: [...state.history[newIndex]],
        historyIndex: newIndex,
        selectedShapeIds: [],
        mode: 'idle',
      };
    }

    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const newIndex = state.historyIndex + 1;
      return {
        ...state,
        shapes: [...state.history[newIndex]],
        historyIndex: newIndex,
        selectedShapeIds: [],
        mode: 'idle',
      };
    }

    case 'CLEAR_ALL': {
      return {
        ...saveToHistory(state, []),
        selectedShapeIds: [],
        mode: 'idle',
      };
    }

    case 'CLEAR_CHART': {
      const newShapes = state.shapes.filter((shape) => shape.chartId !== action.payload);
      return {
        ...saveToHistory(state, newShapes),
        selectedShapeIds: state.selectedShapeIds.filter((id) => {
          const shape = state.shapes.find((s) => s.id === id);
          return shape?.chartId !== action.payload;
        }),
      };
    }

    default:
      return state;
  }
}

// Context value interface
interface DrawingContextValue {
  // State
  state: DrawingState;

  // Tool operations
  setActiveTool: (tool: DrawingToolType | null) => void;
  setMode: (mode: DrawingMode) => void;
  setActiveChart: (chartId: string | null) => void;

  // Shape operations
  addShape: (shape: DrawingShape) => void;
  updateShape: (id: string, updates: Partial<DrawingShape>) => void;
  deleteShape: (id: string) => void;
  deleteSelectedShapes: () => void;

  // Selection
  selectShape: (id: string, additive?: boolean) => void;
  deselectAll: () => void;

  // Style operations
  updateSelectedStyle: (style: Partial<DrawingShapeStyle>) => void;

  // Temp shape (during drawing)
  setTempShape: (shape: Partial<DrawingShape> | null) => void;
  addTempPoint: (point: DrawingDataPoint) => void;
  clearTempPoints: () => void;

  // Sync
  setSyncEnabled: (enabled: boolean) => void;
  setSyncScope: (scope: DrawingSyncScope) => void;

  // History
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // Clear
  clearAll: () => void;
  clearChart: (chartId: string) => void;

  // Helpers
  getShapesForChart: (chartId: string) => DrawingShape[];
  getSelectedShapes: () => DrawingShape[];
  currentStyle: DrawingShapeStyle;
}

// Create context
const DrawingContext = createContext<DrawingContextValue | null>(null);

// Hook to use the context
export const useDrawingContext = () => {
  const context = useContext(DrawingContext);
  if (!context) {
    throw new Error('useDrawingContext must be used within a DrawingProvider');
  }
  return context;
};

// Optional hook that returns null instead of throwing
export const useDrawingContextOptional = () => {
  return useContext(DrawingContext);
};

// Provider props
interface DrawingProviderProps {
  children: React.ReactNode;
  initialStyle?: Partial<DrawingShapeStyle>;
}

// Provider component
export const DrawingProvider: React.FC<DrawingProviderProps> = ({
  children,
  initialStyle,
}) => {
  // Load initial shapes from localStorage
  const initialShapes = useMemo(() => loadShapesFromStorage(), []);

  const [state, dispatch] = useReducer(drawingReducer, {
    ...initialState,
    shapes: initialShapes,
    // Initialize history with loaded shapes
    history: [initialShapes],
    historyIndex: 0,
  });

  // Save shapes to localStorage whenever they change
  useEffect(() => {
    saveShapesToStorage(state.shapes);
  }, [state.shapes]);

  // Current style (from first selected shape or default)
  const currentStyle = useMemo(() => {
    if (state.selectedShapeIds.length > 0) {
      const firstSelected = state.shapes.find(
        (s) => s.id === state.selectedShapeIds[0]
      );
      if (firstSelected) return firstSelected.style;
    }
    return { ...DEFAULT_SHAPE_STYLE, ...initialStyle };
  }, [state.selectedShapeIds, state.shapes, initialStyle]);

  // Tool operations
  const setActiveTool = useCallback((tool: DrawingToolType | null) => {
    dispatch({ type: 'SET_TOOL', payload: tool });
  }, []);

  const setMode = useCallback((mode: DrawingMode) => {
    dispatch({ type: 'SET_MODE', payload: mode });
  }, []);

  const setActiveChart = useCallback((chartId: string | null) => {
    dispatch({ type: 'SET_ACTIVE_CHART', payload: chartId });
  }, []);

  // Shape operations
  const addShape = useCallback((shape: DrawingShape) => {
    dispatch({ type: 'ADD_SHAPE', payload: shape });
  }, []);

  const updateShape = useCallback((id: string, updates: Partial<DrawingShape>) => {
    dispatch({ type: 'UPDATE_SHAPE', payload: { id, updates } });
  }, []);

  const deleteShape = useCallback((id: string) => {
    dispatch({ type: 'DELETE_SHAPE', payload: id });
  }, []);

  const deleteSelectedShapes = useCallback(() => {
    dispatch({ type: 'DELETE_SELECTED' });
  }, []);

  // Selection
  const selectShape = useCallback((id: string, additive = false) => {
    dispatch({ type: 'SELECT_SHAPE', payload: { id, additive } });
  }, []);

  const deselectAll = useCallback(() => {
    dispatch({ type: 'DESELECT_ALL' });
  }, []);

  // Style operations
  const updateSelectedStyle = useCallback((style: Partial<DrawingShapeStyle>) => {
    dispatch({ type: 'UPDATE_SELECTED_STYLE', payload: style });
  }, []);

  // Temp shape operations
  const setTempShape = useCallback((shape: Partial<DrawingShape> | null) => {
    dispatch({ type: 'SET_TEMP_SHAPE', payload: shape });
  }, []);

  const addTempPoint = useCallback((point: DrawingDataPoint) => {
    dispatch({ type: 'ADD_TEMP_POINT', payload: point });
  }, []);

  const clearTempPoints = useCallback(() => {
    dispatch({ type: 'CLEAR_TEMP_POINTS' });
  }, []);

  // Sync operations
  const setSyncEnabled = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_SYNC_ENABLED', payload: enabled });
  }, []);

  const setSyncScope = useCallback((scope: DrawingSyncScope) => {
    dispatch({ type: 'SET_SYNC_SCOPE', payload: scope });
  }, []);

  // History operations
  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' });
  }, []);

  const canUndo = state.historyIndex > 0;
  const canRedo = state.historyIndex < state.history.length - 1;

  // Clear operations
  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  const clearChart = useCallback((chartId: string) => {
    dispatch({ type: 'CLEAR_CHART', payload: chartId });
  }, []);

  // Helper functions
  const getShapesForChart = useCallback(
    (chartId: string) => {
      if (state.syncEnabled && state.syncScope === 'all') {
        return state.shapes;
      }
      return state.shapes.filter((shape) => shape.chartId === chartId);
    },
    [state.shapes, state.syncEnabled, state.syncScope]
  );

  const getSelectedShapes = useCallback(() => {
    return state.shapes.filter((shape) => state.selectedShapeIds.includes(shape.id));
  }, [state.shapes, state.selectedShapeIds]);

  // Context value
  const value = useMemo<DrawingContextValue>(
    () => ({
      state,
      setActiveTool,
      setMode,
      setActiveChart,
      addShape,
      updateShape,
      deleteShape,
      deleteSelectedShapes,
      selectShape,
      deselectAll,
      updateSelectedStyle,
      setTempShape,
      addTempPoint,
      clearTempPoints,
      setSyncEnabled,
      setSyncScope,
      undo,
      redo,
      canUndo,
      canRedo,
      clearAll,
      clearChart,
      getShapesForChart,
      getSelectedShapes,
      currentStyle,
    }),
    [
      state,
      setActiveTool,
      setMode,
      setActiveChart,
      addShape,
      updateShape,
      deleteShape,
      deleteSelectedShapes,
      selectShape,
      deselectAll,
      updateSelectedStyle,
      setTempShape,
      addTempPoint,
      clearTempPoints,
      setSyncEnabled,
      setSyncScope,
      undo,
      redo,
      canUndo,
      canRedo,
      clearAll,
      clearChart,
      getShapesForChart,
      getSelectedShapes,
      currentStyle,
    ]
  );

  return <DrawingContext.Provider value={value}>{children}</DrawingContext.Provider>;
};
